import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { createRevealSequence } from "../common/Reveal";
import { api, byline, CATEGORY_LABELS, FeatureRequestItem } from "./api";
import BoardLayout from "./BoardLayout";
import VoteButton from "./VoteButton";
import { useVoteToggle } from "./useVoteToggle";

function monthLabel(d: string | Date | null | undefined): string {
  if (!d) return "Undated";
  return new Date(d).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

// Delivered requests grouped by the month they shipped, with the release
// note staff wrote when marking them delivered.
const ChangelogView: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<FeatureRequestItem[] | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await api.changelog());
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to load the changelog");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { toggle, votingId } = useVoteToggle((id, patch) => setItems((prev) => (prev ? prev.map((i) => (i.id === id ? { ...i, ...patch } : i)) : prev)));

  const groups = useMemo(() => {
    const out: { label: string; items: FeatureRequestItem[] }[] = [];
    for (const item of items || []) {
      const label = monthLabel(item.delivered_at || item.status_changed_at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(item);
      else out.push({ label, items: [item] });
    }
    return out;
  }, [items]);

  const enter = createRevealSequence();

  return (
    <BoardLayout subtitle="Everything that's been delivered, newest first.">
      {!items ? (
        <div className="animate-pulse space-y-3 max-w-3xl mx-auto">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 rounded-lg bg-base-300" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-base-300 bg-base-100 p-12 text-center max-w-3xl mx-auto">
          <CheckCircleIcon className="size-10 mx-auto text-base-content/30 mb-3" />
          <p className="font-medium text-base-content">Nothing delivered yet</p>
          <p className="text-sm text-base-content/60">Requests show up here once they ship.</p>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto space-y-8">
          {groups.map((g) => (
            <section key={g.label} className="space-y-3">
              <h2 {...enter()} className="reveal text-[11px] font-semibold uppercase tracking-wider text-base-content/50">
                {g.label}
              </h2>
              {g.items.map((item) => (
                <article
                  key={item.id}
                  {...enter()}
                  onClick={() => navigate(`/feature-requests/${item.id}`)}
                  className="reveal p-4 border rounded-lg bg-base-100 border-base-300 transition-all group cursor-pointer hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <VoteButton size="sm" count={item.vote_count} voted={item.hasVoted} busy={votingId === item.id} onToggle={() => toggle(item)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-sm group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors">{item.title}</h3>
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400">
                          <CheckCircleIcon className="size-3" /> Delivered {item.delivered_at ? new Date(item.delivered_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-base-200 text-base-content/60">{CATEGORY_LABELS[item.category]}</span>
                      </div>
                      {item.release_note ? (
                        <p className="text-sm text-base-content/80 mt-1.5 whitespace-pre-line">{item.release_note}</p>
                      ) : (
                        <p className="text-xs text-base-content/60 mt-1 line-clamp-2 whitespace-pre-line">{item.body}</p>
                      )}
                      <p className="text-xs text-base-content/40 mt-2">Requested by {byline(item)}</p>
                    </div>
                  </div>
                </article>
              ))}
            </section>
          ))}
        </div>
      )}
    </BoardLayout>
  );
};

export default ChangelogView;
